// previewFlags.jsx — the preview gate for in-progress work.
//
// One switch: `usePreviewAccess()`. While the Colectiv design-system port is
// underway (CODERATED_PLAN.md Phase 1), everything new renders only for the
// people on the admin allowlist; everyone else keeps the shipped experience.
//
// ─────────────────────────────────────────────────────────────────────────────
// WHY NOT `user.id === 1`, which is what was asked for:
//
// It does not identify anyone in this codebase. In auth.jsx the login handler
// reads
//
//     const userData = response.user || { id: 1, email, ... }
//
// so `id: 1` is a HARDCODED FALLBACK applied whenever the backend omits a user
// object — not a real account id. And the backend currently boots without a
// database (`invalid dsn: invalid connection option "DB_HOST"`) and serves demo
// responses, so that fallback is the common path, not the rare one.
//
// Gating on `id === 1` today would therefore show the unreleased work to EVERY
// logged-in visitor while looking, in code review, like it was locked down.
// That's the worst kind of gate: one that reads as closed and is open.
//
// So the gate keys on the email allowlist, which is real identity. Once the
// backend returns genuine user rows, an `id === 1` check can be added as an
// ADDITIONAL condition — but it must never be the only one.
// ─────────────────────────────────────────────────────────────────────────────
import { useMemo } from 'react';
import { useAuth } from './auth.jsx';
import { isPlatformAdmin } from './auth.jsx';

const OVERRIDE_KEY = 'coderated:preview';

// Read the ?preview= override once per call. `?preview=1` opts a browser in,
// `?preview=0` opts it back out, and the choice persists so you aren't
// re-appending the param on every navigation.
//
// This is a LOCAL convenience only — it turns on unreleased UI for whoever
// types it, so it must never gate anything sensitive. It controls which
// stylesheet you see, nothing more. Real access control belongs on the server.
function readOverride() {
  if (typeof window === 'undefined') return null;

  try {
    const param = new URLSearchParams(window.location.search).get('preview');
    if (param === '1' || param === 'true') {
      window.localStorage.setItem(OVERRIDE_KEY, '1');
      return true;
    }
    if (param === '0' || param === 'false') {
      window.localStorage.removeItem(OVERRIDE_KEY);
      return false;
    }
    return window.localStorage.getItem(OVERRIDE_KEY) === '1' ? true : null;
  } catch {
    // Private browsing / storage disabled — fail closed, never throw.
    return null;
  }
}

/**
 * True when the current viewer should see in-progress work.
 *
 * Defaults CLOSED: while auth is still resolving, `user` is null and this
 * returns false, so a page render during load shows the shipped UI rather than
 * flashing the unreleased one and then yanking it back.
 */
export function usePreviewAccess() {
  const { user, loading } = useAuth();
  const email = user?.email;

  return useMemo(() => {
    if (isPlatformAdmin(email)) return true;
    // Only consult the manual override once auth has settled, so a slow
    // /me request can't briefly reveal the new UI to a signed-out visitor.
    if (loading) return false;
    return readOverride() === true;
  }, [email, loading]);
}

/**
 * Declarative form. Renders `children` for gated viewers, `fallback` otherwise.
 *
 *   <PreviewGate fallback={<AboutLegacy />}>
 *     <About />
 *   </PreviewGate>
 */
export function PreviewGate({ children, fallback = null }) {
  return usePreviewAccess() ? children : fallback;
}

export default usePreviewAccess;
